// src/p4m_db.js
// ─────────────────────────────────────────────────────────────
// All database operations for P4M
// Import this in App.jsx and replace useState mock data calls
// ─────────────────────────────────────────────────────────────
import { supabase } from './supabase';

// ── AUTH ──────────────────────────────────────────────────────

export const signUp = async ({ name, email, password, role, team, clubId }) => {
  // 1. Create auth user
  const { data: authData, error: authError } = await supabase.auth.signUp({
    email, password,
  });
  if (authError) throw authError;

  // 2. Create member record
  const { data, error } = await supabase.from('members').insert({
    club_id:  clubId,
    auth_id:  authData.user.id,
    name, email, role, team,
    approved: role === 'admin',
  }).select().single();
  if (error) throw error;
  return data;
};

export const signIn = async ({ email, password }) => {
  const { data, error } = await supabase.auth.signInWithPassword({ email, password });
  if (error) throw error;
  return data;
};

export const signOut = async () => {
  await supabase.auth.signOut();
};

export const getCurrentMember = async () => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return null;
  const { data } = await supabase.from('members')
    .select('*').eq('auth_id', user.id).single();
  return data;
};

// ── CLUB ──────────────────────────────────────────────────────

export const createClub = async (clubData) => {
  const { data, error } = await supabase.from('clubs').insert({
    name:          clubData.name,
    sport:         clubData.sport,
    primary_col:   clubData.primary,
    secondary_col: clubData.secondary,
    logo_url:      clubData.logo,
    logo_pos:      clubData.logoPos || 'center center',
    ethos:         clubData.ethos,
    mens:          clubData.mens || 0,
    ladies:        clubData.ladies || 0,
    junior_ages:   clubData.juniorAges || [],
    swim_groups:   clubData.swimGroups || [],
  }).select().single();
  if (error) throw error;
  return data;
};

export const getClub = async (clubId) => {
  const { data, error } = await supabase.from('clubs')
    .select('*').eq('id', clubId).single();
  if (error) throw error;
  return data;
};

export const updateClub = async (clubId, updates) => {
  const { data, error } = await supabase.from('clubs')
    .update(updates).eq('id', clubId).select().single();
  if (error) throw error;
  return data;
};

// ── MEMBERS ───────────────────────────────────────────────────

export const getMembers = async (clubId) => {
  const { data, error } = await supabase.from('members')
    .select('*').eq('club_id', clubId).order('name');
  if (error) throw error;
  return data || [];
};

export const approveMember = async (memberId) => {
  const { error } = await supabase.from('members')
    .update({ approved: true }).eq('id', memberId);
  if (error) throw error;
};

export const updateMemberRole = async (memberId, role) => {
  const { error } = await supabase.from('members')
    .update({ role }).eq('id', memberId);
  if (error) throw error;
};

// ── PLAYERS ───────────────────────────────────────────────────

export const getPlayers = async (clubId) => {
  const { data, error } = await supabase.from('players')
    .select('*').eq('club_id', clubId).order('name');
  if (error) throw error;
  return data || [];
};

export const createPlayer = async (clubId, playerData) => {
  const { data, error } = await supabase.from('players').insert({
    club_id:   clubId,
    name:      playerData.name,
    team:      playerData.team,
    position:  playerData.position,
    events:    playerData.events || [],
    photo_url: playerData.photo,
    scores:    playerData.scores || {},
    fitness:   playerData.fitness || {},
    pin:       playerData.pin,
  }).select().single();
  if (error) throw error;
  return data;
};

export const updatePlayer = async (playerId, updates) => {
  const mapped = {
    name:          updates.name,
    team:          updates.team,
    position:      updates.position,
    events:        updates.events,
    photo_url:     updates.photo,
    scores:        updates.scores,
    fitness:       updates.fitness,
    last_assessed: updates.lastAssessed,
  };
  // Remove undefined keys
  Object.keys(mapped).forEach(k => mapped[k] === undefined && delete mapped[k]);
  const { data, error } = await supabase.from('players')
    .update(mapped).eq('id', playerId).select().single();
  if (error) throw error;
  return data;
};

export const deletePlayer = async (playerId) => {
  const { error } = await supabase.from('players').delete().eq('id', playerId);
  if (error) throw error;
};

export const uploadPlayerPhoto = async (clubId, playerId, base64Data) => {
  // Convert base64 to blob
  const res = await fetch(base64Data);
  const blob = await res.blob();
  const path = `${clubId}/players/${playerId}.jpg`;
  const { error } = await supabase.storage.from('photos').upload(path, blob, { upsert: true });
  if (error) throw error;
  const { data } = supabase.storage.from('photos').getPublicUrl(path);
  return data.publicUrl;
};

// ── FIXTURES ──────────────────────────────────────────────────

export const getFixtures = async (clubId) => {
  const { data, error } = await supabase.from('fixtures')
    .select('*').eq('club_id', clubId).order('date');
  if (error) throw error;
  return data || [];
};

export const createFixture = async (clubId, fixData) => {
  const { data, error } = await supabase.from('fixtures').insert({
    club_id:      clubId,
    opponent:     fixData.opponent,
    date:         fixData.date,
    time:         fixData.time,
    location:     fixData.location,
    team:         fixData.team,
    teams:        fixData.teams || [],
    is_home:      fixData.isHome !== false,
    is_session:   fixData.isSession || false,
    availability: {},
  }).select().single();
  if (error) throw error;
  return data;
};

export const updateFixture = async (fixtureId, updates) => {
  const { data, error } = await supabase.from('fixtures')
    .update(updates).eq('id', fixtureId).select().single();
  if (error) throw error;
  return data;
};

export const setAvailability = async (fixtureId, memberId, status) => {
  // Read current, merge, write back
  const { data: fix } = await supabase.from('fixtures')
    .select('availability').eq('id', fixtureId).single();
  const updated = { ...(fix?.availability || {}), [memberId]: status };
  const { error } = await supabase.from('fixtures')
    .update({ availability: updated }).eq('id', fixtureId);
  if (error) throw error;
};

// ── LINEUPS ───────────────────────────────────────────────────

export const getLineups = async (clubId) => {
  const { data, error } = await supabase.from('lineups')
    .select('*').eq('club_id', clubId);
  if (error) throw error;
  // Return as {fixtureId: lineup} map to match existing app state shape
  const map = {};
  (data || []).forEach(l => {
    map[l.fixture_id] = {
      formation: l.formation,
      slotMap:   l.slot_map,
      starters:  l.starters,
      subs:      l.subs,
    };
  });
  return map;
};

export const saveLineup = async (clubId, fixtureId, lineupData) => {
  const { error } = await supabase.from('lineups').upsert({
    club_id:    clubId,
    fixture_id: fixtureId,
    formation:  lineupData.formation,
    slot_map:   lineupData.slotMap || {},
    starters:   lineupData.starters || [],
    subs:       lineupData.subs || [],
  }, { onConflict: 'fixture_id' });
  if (error) throw error;
};

// ── POSTS ─────────────────────────────────────────────────────

export const getPosts = async (clubId) => {
  const { data, error } = await supabase.from('posts')
    .select('*, comments(*)')
    .eq('club_id', clubId)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return data || [];
};

export const createPost = async (clubId, authorId, postData) => {
  const { data, error } = await supabase.from('posts').insert({
    club_id:     clubId,
    author_id:   authorId,
    author_name: postData.author,
    body:        postData.body,
    tag:         postData.tag,
    team:        postData.team,
    photo_url:   postData.photo,
    pinned:      postData.pinned || false,
    reactions:   {},
  }).select().single();
  if (error) throw error;
  return data;
};

export const addComment = async (postId, authorId, authorName, body) => {
  const { data, error } = await supabase.from('comments').insert({
    post_id: postId, author_id: authorId, author_name: authorName, body,
  }).select().single();
  if (error) throw error;
  return data;
};

export const toggleReaction = async (postId, emoji, memberId) => {
  const { data: post } = await supabase.from('posts')
    .select('reactions').eq('id', postId).single();
  const reactions = post?.reactions || {};
  const current = reactions[emoji] || [];
  const updated = current.includes(memberId)
    ? current.filter(id => id !== memberId)
    : [...current, memberId];
  reactions[emoji] = updated;
  const { error } = await supabase.from('posts')
    .update({ reactions }).eq('id', postId);
  if (error) throw error;
};

// ── FEEDBACK ──────────────────────────────────────────────────

export const getFeedback = async (playerId) => {
  const { data, error } = await supabase.from('feedback')
    .select('*').eq('player_id', playerId).order('date', { ascending: false });
  if (error) throw error;
  return data || [];
};

export const addFeedback = async (clubId, playerId, feedbackData) => {
  const { data, error } = await supabase.from('feedback').insert({
    club_id:        clubId,
    player_id:      playerId,
    match_name:     feedbackData.match || feedbackData.custom,
    date:           feedbackData.date,
    result:         feedbackData.result,
    goals_for:      feedbackData.goalsFor,
    goals_against:  feedbackData.goalsAgainst,
    note:           feedbackData.note,
    races:          feedbackData.races || [],
    type:           feedbackData.type || 'match',
  }).select().single();
  if (error) throw error;
  return data;
};

export const addPlayerResponse = async (feedbackId, response) => {
  const { error } = await supabase.from('feedback')
    .update({ player_response: response }).eq('id', feedbackId);
  if (error) throw error;
};

// ── PROGRAMMES ────────────────────────────────────────────────

export const saveProgram = async (clubId, playerId, sport, programData) => {
  const { error } = await supabase.from('programmes').upsert({
    club_id:   clubId,
    player_id: playerId,
    sport,
    overview:  programData.overview,
    weeks:     programData.weeks || [],
  }, { onConflict: 'player_id' });
  if (error) throw error;
};

export const getProgram = async (playerId) => {
  const { data, error } = await supabase.from('programmes')
    .select('*').eq('player_id', playerId).single();
  if (error) return null;
  return data;
};

// ── EVENTS ────────────────────────────────────────────────────

export const getEvents = async (clubId) => {
  const { data, error } = await supabase.from('events')
    .select('*').eq('club_id', clubId).order('date');
  if (error) throw error;
  return data || [];
};

export const createEvent = async (clubId, eventData) => {
  const { data, error } = await supabase.from('events').insert({
    club_id: clubId, ...eventData,
  }).select().single();
  if (error) throw error;
  return data;
};

// ── TOURNAMENTS ───────────────────────────────────────────────

export const getTournaments = async (clubId) => {
  const { data, error } = await supabase.from('tournaments')
    .select('*').eq('club_id', clubId).order('date');
  if (error) throw error;
  return data || [];
};

export const createTournament = async (clubId, tData) => {
  const { data, error } = await supabase.from('tournaments').insert({
    club_id: clubId, ...tData,
  }).select().single();
  if (error) throw error;
  return data;
};

// ── PAYMENTS ──────────────────────────────────────────────────

export const getPayments = async (clubId) => {
  const { data, error } = await supabase.from('payments')
    .select('*').eq('club_id', clubId).order('created_at', { ascending: false });
  if (error) throw error;
  return data || [];
};

export const createPayment = async (clubId, paymentData) => {
  const { data, error } = await supabase.from('payments').insert({
    club_id: clubId, ...paymentData,
  }).select().single();
  if (error) throw error;
  return data;
};

export const updatePaymentStatus = async (paymentId, status) => {
  const { error } = await supabase.from('payments')
    .update({ status }).eq('id', paymentId);
  if (error) throw error;
};

// ── SHOP ──────────────────────────────────────────────────────

export const getShopItems = async (clubId) => {
  const { data, error } = await supabase.from('shop_items')
    .select('*').eq('club_id', clubId).eq('active', true);
  if (error) throw error;
  return data || [];
};

export const createShopItem = async (clubId, item) => {
  const { data, error } = await supabase.from('shop_items').insert({
    club_id: clubId, ...item,
  }).select().single();
  if (error) throw error;
  return data;
};

export const createOrder = async (clubId, memberId, memberName, items) => {
  const total = items.reduce((s, i) => s + i.price * (i.qty || 1), 0);
  const { data, error } = await supabase.from('shop_orders').insert({
    club_id: clubId, member_id: memberId, member_name: memberName, items, total,
  }).select().single();
  if (error) throw error;
  return data;
};

// ── SWIM FRAMEWORK ────────────────────────────────────────────

export const saveSwimFramework = async (clubId, squad, level, planData) => {
  const { error } = await supabase.from('swim_frameworks').upsert({
    club_id:     clubId,
    squad,
    level,
    overview:    planData.overview,
    phases:      planData.phases || [],
    zones:       planData.zones || [],
    wizard_data: planData.wizardData || {},
  }, { onConflict: 'club_id,squad' });
  if (error) throw error;
};

export const getSwimFramework = async (clubId, squad) => {
  const { data } = await supabase.from('swim_frameworks')
    .select('*').eq('club_id', clubId).eq('squad', squad).single();
  return data;
};

// ── LEAGUE TABLE ──────────────────────────────────────────────

export const saveLeagueTable = async (clubId, rows) => {
  // Delete existing and reinsert
  await supabase.from('league_table').delete().eq('club_id', clubId);
  if (!rows.length) return;
  const { error } = await supabase.from('league_table').insert(
    rows.map(r => ({ club_id: clubId, ...r }))
  );
  if (error) throw error;
};

export const getLeagueTable = async (clubId) => {
  const { data, error } = await supabase.from('league_table')
    .select('*').eq('club_id', clubId).order('points', { ascending: false });
  if (error) throw error;
  return data || [];
};

// ── STORAGE BUCKET SETUP NOTE ─────────────────────────────────
// In Supabase dashboard → Storage → New bucket → name: "photos" → Public: ON
// This enables player photo and club logo uploads
